import { AppShell, Button, cn, RoleChip } from "@jod/ui";
import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import {
  AlertTriangle,
  BarChart3,
  CalendarClock,
  ClipboardList,
  LayoutDashboard,
  LayoutGrid,
  ListOrdered,
  Monitor,
  QrCode,
  ShieldCheck,
  SlidersHorizontal,
  Store,
  Table2,
  Users,
  Wine,
} from "lucide-react";
import type { ReactNode } from "react";
import { useOwnerState, useSwitchVenue } from "../features/backoffice/queries.ts";
import {
  getOwnerPrincipal,
  isOwnerAuthenticated,
  ownerMustChangePassword,
  signOutOwner,
} from "../lib/auth-session.ts";

export const Route = createFileRoute("/_app")({
  beforeLoad: () => {
    if (!isOwnerAuthenticated()) throw redirect({ to: "/login" });
    if (ownerMustChangePassword()) throw redirect({ to: "/change-password" });
  },
  component: AppLayout,
});

type NavPath =
  | "/dashboard"
  | "/venue"
  | "/branches"
  | "/zones"
  | "/tables"
  | "/layout"
  | "/qr"
  | "/menu"
  | "/social-rules"
  | "/sessions"
  | "/staff"
  | "/roles"
  | "/analytics"
  | "/incidents"
  | "/screen"
  | "/settings/privacy"
  | "/settings/retention";
const NAV: { key: string; label: string; to: NavPath; icon: ReactNode }[] = [
  { key: "dashboard", label: "Dashboard", to: "/dashboard", icon: <LayoutDashboard /> },
  { key: "venue", label: "Venue", to: "/venue", icon: <Store /> },
  { key: "branches", label: "Branches", to: "/branches", icon: <ClipboardList /> },
  { key: "zones", label: "Zones", to: "/zones", icon: <SlidersHorizontal /> },
  { key: "tables", label: "Tables", to: "/tables", icon: <Table2 /> },
  { key: "layout", label: "Layout", to: "/layout", icon: <LayoutGrid /> },
  { key: "qr", label: "QR", to: "/qr", icon: <QrCode /> },
  { key: "menu", label: "Menu", to: "/menu", icon: <Wine /> },
  { key: "social", label: "Social Rules", to: "/social-rules", icon: <ShieldCheck /> },
  { key: "sessions", label: "Sessions", to: "/sessions", icon: <CalendarClock /> },
  { key: "staff", label: "Staff", to: "/staff", icon: <Users /> },
  { key: "roles", label: "Roles", to: "/roles", icon: <ListOrdered /> },
  { key: "analytics", label: "Analytics", to: "/analytics", icon: <BarChart3 /> },
  { key: "incidents", label: "Incidents", to: "/incidents", icon: <AlertTriangle /> },
  { key: "screen", label: "Venue Screen", to: "/screen", icon: <Monitor /> },
  { key: "privacy", label: "Privacy", to: "/settings/privacy", icon: <ShieldCheck /> },
  { key: "retention", label: "Retention", to: "/settings/retention", icon: <CalendarClock /> },
];

function AppLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const principal = getOwnerPrincipal();
  const { data } = useOwnerState();
  const switchVenue = useSwitchVenue();
  const currentVenue = data?.venues.find((venue) => venue.id === data.currentVenueId);
  // Hide the switcher entirely for single-venue owners — it would just be a
  // one-item dropdown that does nothing (FR-OWN-002).
  const hasMultipleVenues = (data?.venues.length ?? 0) > 1;
  const sidebar = (
    <>
      <div className="px-2 pb-4">
        <p className="font-display font-semibold text-lg leading-tight">Just One Drink</p>
        <p className="text-muted-foreground text-xs">Owner Admin · Backoffice</p>
      </div>
      <nav className="flex flex-col gap-1">
        {NAV.map((item) => (
          <button
            type="button"
            key={item.key}
            onClick={() => navigate({ to: item.to })}
            className={cn(
              "flex items-center gap-3 rounded-md px-3 py-2 text-left text-sm [&_svg]:size-4",
              pathname.startsWith(item.to)
                ? "bg-primary/10 font-medium text-primary"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            {item.icon}
            {item.label}
          </button>
        ))}
      </nav>
    </>
  );
  const topbar = (
    <div className="flex w-full flex-wrap items-center justify-between gap-3">
      <div>
        {hasMultipleVenues ? (
          <select
            aria-label="Switch venue"
            value={data?.currentVenueId ?? ""}
            onChange={(event) => switchVenue.mutate(event.target.value)}
            className="rounded-md border border-input bg-transparent px-2 py-1 font-display font-medium text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {data?.venues.map((venue) => (
              <option key={venue.id} value={venue.id}>
                {venue.name}
              </option>
            ))}
          </select>
        ) : (
          <span className="font-display font-medium text-sm">{currentVenue?.name ?? "Venue"}</span>
        )}
        <p className="text-muted-foreground text-xs">{currentVenue?.nameTh}</p>
      </div>
      <div className="flex items-center gap-2">
        {principal ? <RoleChip label={principal.role} /> : null}
        <Button
          variant="ghost"
          size="sm"
          className="text-foreground"
          onClick={() => {
            signOutOwner();
            void navigate({ to: "/login" });
          }}
        >
          Sign out
        </Button>
      </div>
    </div>
  );
  return (
    <div className="min-h-dvh bg-background text-foreground">
      <AppShell sidebar={sidebar} topbar={topbar}>
        <Outlet />
      </AppShell>
    </div>
  );
}
